import { Box, CircularProgress, Collapse } from '@mui/material';
import { EntityState } from '@reduxjs/toolkit';
import { useEffect, useState } from 'react';

import { PageSidebarItem } from '@/containers';
import { LibraryCategoryEntity } from '@/store';

export interface SelectedCategoryInfo {
  categorySlug: string | null;
  subCategorySlug: string | null;
}

interface CategoryTreeProps {
  selectedItem: SelectedCategoryInfo;
  onSelectItem: (item: SelectedCategoryInfo) => void;
  categories?: EntityState<LibraryCategoryEntity, string>;
}

export const CategoryTree = ({
  selectedItem,
  onSelectItem,
  categories,
}: CategoryTreeProps) => {
  /* 
    NOTE: This used to allow for multiple categories to be expanded at once,
    have kept the mechanism in place in case we want to re-allow that in the future
  */
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  useEffect(() => {
    // Sync expanded categories with selected category
    setExpandedCategories(
      selectedItem.categorySlug
        ? new Set([selectedItem.categorySlug])
        : new Set(),
    );
  }, [selectedItem.categorySlug]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {!categories ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress
            size={60}
            thickness={6}
            sx={{ color: 'accent.main' }}
          />
        </Box>
      ) : (
        Object.values(categories?.entities || {}).map(category => {
          const subCategories = Object.values(category.subCategories.entities);
          const hasSubCategories = subCategories.length > 0;
          const isExpanded = expandedCategories.has(category.slug);
          const isSelected = selectedItem.categorySlug === category.slug;

          return (
            <Box
              key={category.slug}
              sx={[isExpanded && styles.selectedTopicCard]}>
              <PageSidebarItem
                onClick={() => {
                  onSelectItem({
                    categorySlug: category.slug,
                    subCategorySlug: null,
                  });
                }}
                label={category.name ?? category.slug}
                isActive={isSelected && !selectedItem.subCategorySlug}
                count={category.distinctEpisodeCount}
                expanded={hasSubCategories ? isExpanded : undefined}
              />
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                {subCategories.map(subCategory => (
                  <PageSidebarItem
                    key={subCategory.slug}
                    onClick={() =>
                      onSelectItem({
                        categorySlug: category.slug,
                        subCategorySlug: subCategory.slug,
                      })
                    }
                    label={subCategory.name ?? subCategory.slug}
                    isActive={selectedItem.subCategorySlug === subCategory.slug}
                    count={subCategory.episodeCount}
                    nested
                  />
                ))}
              </Collapse>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export const styles = {
  selectedTopicCard: {
    backgroundColor: 'white',
  },
};
